(function () {
  const TURNSTILE_API_URL =
    "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
  const WIDGET_SELECTOR = "[data-turnstile-widget]";
  const VALID_SIZES = new Set(["normal", "compact", "flexible"]);

  const config = window.theme?.turnstile;
  if (!config?.enabled || !config.siteKey) return;

  if (window.theme.turnstile?.getToken) {
    window.theme.turnstile.renderWidgets?.();
    return;
  }

  let apiPromise = null;
  let pendingChallenge = null;
  const widgets = new Map();

  function finishChallenge(error, token) {
    if (!pendingChallenge) return;

    const { resolve, reject, timeoutId } = pendingChallenge;
    window.clearTimeout(timeoutId);
    pendingChallenge = null;

    if (error) {
      reject(error);
      return;
    }

    resolve(token);
  }

  function loadApi() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (apiPromise) return apiPromise;

    apiPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector(
        `script[src="${TURNSTILE_API_URL}"]`,
      );

      if (existingScript) {
        existingScript.addEventListener("load", () =>
          resolve(window.turnstile),
        );
        existingScript.addEventListener("error", reject);
        return;
      }

      const script = document.createElement("script");
      script.src = TURNSTILE_API_URL;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve(window.turnstile);
      script.onerror = reject;
      document.head.appendChild(script);
    });

    return apiPromise;
  }

  function getWidgetContainer(container) {
    if (container instanceof HTMLElement) return container;
    if (typeof container === "string") return document.querySelector(container);

    return document.querySelector(WIDGET_SELECTOR);
  }

  function getWidgetConfig(container) {
    const requestedSize = container.dataset.turnstileSize || "normal";
    const isInvisible =
      container.dataset.turnstileMode === "invisible" ||
      requestedSize === "invisible";
    const size = VALID_SIZES.has(requestedSize) ? requestedSize : "normal";
    const deferredExecution =
      isInvisible || container.dataset.turnstileExecution === "execute";

    return { size, isInvisible, deferredExecution };
  }

  async function renderWidgets() {
    const turnstile = await loadApi();
    const containers = document.querySelectorAll(WIDGET_SELECTOR);

    containers.forEach((container) => {
      if (widgets.has(container)) return;

      const { size, isInvisible, deferredExecution } =
        getWidgetConfig(container);
      const state = {
        id: null,
        isInvisible,
        token: "",
        tokenDelivered: false,
      };
      const renderOptions = {
        sitekey: config.siteKey,
        size,
        callback: (token) => {
          state.token = token;
          state.tokenDelivered = false;
          finishChallenge(null, token);
        },
        "expired-callback": () => {
          state.token = "";
          state.tokenDelivered = false;
          finishChallenge(new Error(config.errorMessage));
        },
        "error-callback": () => {
          state.token = "";
          state.tokenDelivered = false;
          finishChallenge(new Error(config.errorMessage));
        },
      };

      if (deferredExecution) {
        renderOptions.execution = "execute";
        renderOptions.appearance = "interaction-only";
      }

      if (container.dataset.turnstileAction) {
        renderOptions.action = container.dataset.turnstileAction;
      }

      state.id = turnstile.render(container, renderOptions);
      widgets.set(container, state);
    });
  }

  function askUserToComplete(container) {
    container.scrollIntoView({ behavior: "smooth", block: "center" });
    container.focus?.({ preventScroll: true });
    throw new Error(config.errorMessage);
  }

  function resetWidget(turnstile, widget) {
    widget.token = "";
    widget.tokenDelivered = false;

    try {
      turnstile.reset(widget.id);
    } catch (error) {
      console.error(error);
    }
  }

  async function getToken(options = {}) {
    if (!config.enabled || !config.siteKey) return "";

    if (pendingChallenge) {
      throw new Error(config.errorMessage);
    }

    const turnstile = await loadApi();
    await renderWidgets();

    const container = getWidgetContainer(options.container);
    const widget = container ? widgets.get(container) : null;

    if (!container || !widget) {
      throw new Error(config.errorMessage);
    }

    const response = turnstile.getResponse?.(widget.id) || widget.token;
    if (response && !widget.tokenDelivered) {
      widget.tokenDelivered = true;
      return response;
    }

    if (!widget.isInvisible) {
      if (response) resetWidget(turnstile, widget);
      askUserToComplete(container);
    }

    const token = await new Promise((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        finishChallenge(new Error(config.errorMessage));
      }, 30000);

      pendingChallenge = { resolve, reject, timeoutId };

      try {
        resetWidget(turnstile, widget);
        turnstile.execute(widget.id);
      } catch (error) {
        finishChallenge(new Error(config.errorMessage));
      }
    });

    widget.tokenDelivered = true;
    return token;
  }

  window.theme.turnstile = {
    ...config,
    getToken,
    renderWidgets,
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      renderWidgets().catch((error) => console.error(error));
    });
  } else {
    renderWidgets().catch((error) => console.error(error));
  }
})();
